import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import CustomButton from '../../../components/CustomButton';
import InputField from '../../../components/ui/InputField';
import SingleDatePicker from '../../../components/ui/SingleDatePicker';
import {
    SpaceAgreement,
    SpaceAgreementWriteBody,
} from '../../../api/organization/organizationApiTypes';

/**
 * Record or edit a space agreement (#2165 §3).
 *
 * This exists for the case the whole feature is about: **the contract is
 * concluded somewhere else** — on paper, in the tenant's own ERP — and kolula
 * needs to know it exists, what it is called, and when it ends. It is not a
 * contract editor; it captures identity and term.
 *
 * Two things here are deliberate and easy to "simplify" wrongly:
 *
 * 1. **The term is a CHOICE, never a blank field.** *befristet bis [date]* or
 *    *unbefristet* — one of them is picked. A blank date cannot be told apart
 *    from "nobody filled it in", and that ambiguity is exactly what makes a
 *    stale date dangerous later: the archive guard and the release rules have
 *    to know whether open-ended was meant or merely left empty (ADR-0018 —
 *    absence never carries meaning).
 * 2. **`name` is required and is not `agreementNumber`.** The number is the
 *    reference off the paper; the name is what an operator recognises in a
 *    list — *"Mietvertrag Gateway Factory, Räume 3.01–3.04"*. Typing a name
 *    into the number field reuses a column that means something else.
 *
 * The charging model is **not** collected. It is optional in every
 * invoice-issuer mode (ADR-0008 amendment 2026-10-05, decision 3) and nothing
 * in the product can create one yet — per-offer pricing arrives with #1712 →
 * Services phase 4, and #2180 decided explicitly not to build an interim
 * surface for it. The section around the form states that the invoice to
 * the organization is created outside kolula.
 */

export type AgreementTerm = 'fixed' | 'openEnded';

export interface AgreementFormValue {
    name: string;
    agreementNumber: string;
    note: string;
    startDate: Dayjs | null;
    /** Explicit — there is no "not chosen yet" once the operator submits. */
    term: AgreementTerm;
    expiryDate: Dayjs | null;
}

export const emptyAgreementForm = (): AgreementFormValue => ({
    name: '',
    agreementNumber: '',
    note: '',
    startDate: null,
    // Neither option is preselected as a "safe default": open-ended is a real
    // commercial statement, and defaulting to it would let an operator record
    // a perpetual contract by not reading the form. `fixed` starts selected
    // with an empty date, so submitting without choosing fails loudly.
    term: 'fixed',
    expiryDate: null,
});

/** Fills the form from an existing agreement, for the edit case. */
export const agreementToForm = (agreement: SpaceAgreement): AgreementFormValue => ({
    name: agreement.name ?? '',
    agreementNumber: agreement.agreementNumber ?? '',
    note: agreement.note ?? '',
    startDate: agreement.startDate ? dayjs(agreement.startDate) : null,
    // A stored null expiry IS open-ended — by the time a row exists, the choice
    // was made. Reading it back as `fixed` with an empty date would silently
    // invite the operator to put a date on a perpetual contract.
    term: agreement.expiryDate ? 'fixed' : 'openEnded',
    expiryDate: agreement.expiryDate ? dayjs(agreement.expiryDate) : null,
});

const trimOrNull = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
};

/**
 * Calendar dates, `YYYY-MM-DD` — never a timestamp.
 *
 * A picker in Berlin hands back local midnight; serialised as an instant that
 * is the PREVIOUS day in UTC, and the server reads the calendar day. Sending
 * the calendar date removes the offset from the wire entirely. The cancel
 * endpoint already refuses timestamps for this reason; create and edit send the
 * same shape so the two cannot disagree about what "31.12." means.
 */
const toCalendarDate = (date: Dayjs | null): string | null =>
    (date && date.isValid() ? date.format('YYYY-MM-DD') : null);

/** The form's value → the API body. Returns null when the form is incomplete. */
export const agreementFormToBody = (
    value: AgreementFormValue,
): SpaceAgreementWriteBody | null => {
    if (value.name.trim() === '') return null;
    if (value.term === 'fixed' && !toCalendarDate(value.expiryDate)) return null;
    return {
        name: value.name.trim(),
        agreementNumber: trimOrNull(value.agreementNumber),
        note: trimOrNull(value.note),
        startDate: toCalendarDate(value.startDate),
        // Open-ended is a null the operator PICKED, not a field left empty.
        expiryDate: value.term === 'openEnded' ? null : toCalendarDate(value.expiryDate),
        // `commercialModelId` is deliberately NOT a key here — see the type.
        // An explicit null would blank an existing charging model on edit,
        // because the PATCH validator writes the column whenever the key is
        // present. Absent means "leave it alone".
    };
};

interface OrgAgreementFormProps {
    value: AgreementFormValue;
    onChange: (updates: Partial<AgreementFormValue>) => void;
    onSubmit: () => void;
    onCancel: () => void;
    /** Editing an existing agreement rather than recording a new one. */
    isEdit: boolean;
    isSaving: boolean;
    /** Message from the API, shown verbatim — it names the actual reason. */
    error: string | null;
    /** Set after a failed submit, so the form is quiet until then. */
    showValidation: boolean;
}

const OrgAgreementForm: React.FC<OrgAgreementFormProps> = ({
    value,
    onChange,
    onSubmit,
    onCancel,
    isEdit,
    isSaving,
    error,
    showValidation,
}) => {
    const { t } = useTranslation();

    const nameMissing = showValidation && value.name.trim() === '';
    const expiryMissing = showValidation
        && value.term === 'fixed'
        && !toCalendarDate(value.expiryDate);

    return (
        <div className={'flex flex-col gap-4 rounded-[5px] bg-text-contrast shadow-sm px-5 py-4'}>
            <p className={'text-sm font-medium text-foreground'}>
                {isEdit
                    ? t('organization.agreements.form.edit_title')
                    : t('organization.agreements.form.create_title')}
            </p>

            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                <InputField
                    labelAbove
                    label={t('organization.agreements.form.name')}
                    help={t('organization.agreements.form.name_help')}
                    value={value.name}
                    onChange={(name) => onChange({ name })}
                    error={nameMissing}
                    errorText={nameMissing
                        ? t('organization.agreements.form.name_required')
                        : undefined}
                    disabled={isSaving}
                />
                <InputField
                    labelAbove
                    label={t('organization.agreements.form.number')}
                    help={t('organization.agreements.form.number_help')}
                    value={value.agreementNumber}
                    onChange={(agreementNumber) => onChange({ agreementNumber })}
                    disabled={isSaving}
                />
            </div>

            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                <SingleDatePicker
                    label={t('organization.agreements.form.start_date')}
                    value={value.startDate}
                    onChange={(startDate) => onChange({ startDate })}
                    disabled={isSaving}
                />
            </div>

            {/* The term is picked, never left blank — see the file docblock. */}
            <fieldset className={'flex flex-col gap-2'}>
                <legend className={'text-sm text-foreground/60 mb-1'}>
                    {t('organization.agreements.form.term_legend')}
                </legend>
                <label className={'flex items-center gap-2 text-sm text-foreground'}>
                    <input
                        type={'radio'}
                        name={'agreement-term'}
                        className={'accent-primary'}
                        checked={value.term === 'fixed'}
                        onChange={() => onChange({ term: 'fixed' })}
                        disabled={isSaving}
                    />
                    {t('organization.agreements.form.term_fixed')}
                </label>
                {value.term === 'fixed' && (
                    <div className={'md:w-1/2 ml-6'}>
                        <SingleDatePicker
                            label={t('organization.agreements.form.expiry_date')}
                            value={value.expiryDate}
                            onChange={(expiryDate) => onChange({ expiryDate })}
                            disabled={isSaving}
                        />
                        {expiryMissing && (
                            <p className={'text-sm text-primary mt-1'}>
                                {t('organization.agreements.form.expiry_required')}
                            </p>
                        )}
                    </div>
                )}
                <label className={'flex items-center gap-2 text-sm text-foreground'}>
                    <input
                        type={'radio'}
                        name={'agreement-term'}
                        className={'accent-primary'}
                        checked={value.term === 'openEnded'}
                        onChange={() => onChange({ term: 'openEnded', expiryDate: null })}
                        disabled={isSaving}
                    />
                    {t('organization.agreements.form.term_open_ended')}
                </label>
                <p className={'text-xs text-foreground/60 ml-6'}>
                    {t('organization.agreements.form.term_hint')}
                </p>
            </fieldset>

            <InputField
                labelAbove
                label={t('organization.agreements.form.note')}
                help={t('organization.agreements.form.note_help')}
                value={value.note}
                onChange={(note) => onChange({ note })}
                disabled={isSaving}
            />

            {error && (
                <p className={'text-sm text-foreground'}>{error}</p>
            )}

            <div className={'flex items-center gap-2'}>
                <CustomButton
                    title={isEdit
                        ? t('organization.agreements.form.save')
                        : t('organization.agreements.form.create')}
                    onClick={onSubmit}
                    isFilled
                    loading={isSaving}
                />
                <CustomButton
                    title={t('common.cancel')}
                    onClick={onCancel}
                    isFilled={false}
                    disabled={isSaving}
                />
            </div>
        </div>
    );
};

export default OrgAgreementForm;
